// BUG-2064: the page-level hand-off between a scope's closed change queue and the queue re-opened
// for that scope (an A → B → A tenant switch), so the entry a closed pass is still sending is never
// taken for one a kill left `syncing` and sent a second time. ADR 066 — Offline Mode § 4.6.
import type { OfflineScope } from '../../gateways/native-device';

/**
 * Per scope, the passes of this page's closed queues still waiting on their last request. A pass in
 * another page (a relaunch) leaves nothing here, so a kill's entry is still re-sent.
 */
const settlingByScope = new Map<string, Promise<void>>();

function scopeKey(scope: OfflineScope): string {
  return JSON.stringify([scope.tenantId, scope.userId]);
}

/**
 * Hand a closed queue's running pass to the next queue opened for its scope.
 *
 * @param scope - the closed queue's tenant and user.
 * @param pass - its running pass.
 * @sideEffect Records the pass in the page-level registry until it settles.
 */
export function holdScopeWhileSettling(scope: OfflineScope, pass: Promise<unknown>): void {
  const key = scopeKey(scope);
  const settled = pass.then(
    () => undefined,
    () => undefined,
  );
  const held = Promise.all([settlingByScope.get(key), settled]).then(() => undefined);
  settlingByScope.set(key, held);
  void held.then(() => {
    if (settlingByScope.get(key) === held) settlingByScope.delete(key);
  });
}

/**
 * @param scope - the re-opened queue's tenant and user.
 * @param signal - the pass's abort signal: an abort stops the wait too.
 * @returns null when no closed queue of the scope has a request in flight (the pass starts at
 *   once), else a promise that settles once they have all settled or the signal aborts.
 */
export function closedQueuesSettled(scope: OfflineScope, signal: AbortSignal): Promise<void> | null {
  const held = settlingByScope.get(scopeKey(scope));
  if (held == null) return null;
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    signal.addEventListener('abort', () => resolve(), { once: true });
    void held.then(() => resolve());
  });
}
